// src/utils/deviceTzCheck.js — tzcanada-9b4e2f17
//
// Is THIS device's time-zone data new enough for the company's zone? British Columbia, Alberta,
// Inuvik and Manitoba stop changing their clocks on 2026-11-01 (IANA tzdata 2026b–e). A browser or
// phone still on older rules shows every job time an hour off there from Nov 1, and a job typed on
// it saves an hour off too — the server is right, the device is not. Separately, a Windows update
// can leave a browser unable to name its own zone, so it falls back to UTC (times 4–8 hours off).
//
// checkDeviceTz(companyTz) → null (fine) | { kind: 'stale' | 'unrecognised', message }
// Shown as a strip: web src/components/common/DeviceTzWarning.js, phone src/components/
// DeviceTzWarning.js. This file is BYTE-IDENTICAL in both repos (testOrgTime.js D2 checks). Pure
// apart from Intl, which tests can replace.

// The offset (minutes from UTC) each changed zone must have mid-November 2026 on new rules.
export const PROBE_INSTANT = new Date('2026-11-15T12:00:00.000Z');
export const EXPECTED_OFFSETS = {
    'America/Vancouver': -420,
    'America/Edmonton': -360,
    'America/Inuvik': -360,
    'America/Winnipeg': -300,
};
const PLACE = {
    'America/Vancouver': 'British Columbia',
    'America/Edmonton': 'Alberta',
    'America/Inuvik': 'Inuvik',
    'America/Winnipeg': 'Manitoba',
};
// Canadian zones, for the "this computer can't name its zone" case.
const CANADIAN_ZONES = [
    'America/St_Johns', 'America/Halifax', 'America/Glace_Bay', 'America/Moncton', 'America/Goose_Bay',
    'America/Blanc-Sablon', 'America/Toronto', 'America/Iqaluit', 'America/Atikokan', 'America/Winnipeg',
    'America/Rankin_Inlet', 'America/Resolute', 'America/Regina', 'America/Swift_Current',
    'America/Edmonton', 'America/Cambridge_Bay', 'America/Inuvik', 'America/Vancouver',
    'America/Dawson_Creek', 'America/Fort_Nelson', 'America/Creston', 'America/Whitehorse', 'America/Dawson',
];
const UTC_NAMES = ['UTC', 'Etc/UTC', 'Etc/GMT', 'GMT', 'Etc/Unknown'];

/** Minutes from UTC of `zone` at `instant`, by this device's own Intl; null if it can't tell. */
export function deviceOffsetMinutes(zone, instant) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).formatToParts(instant);
        const v = Object.fromEntries(parts.map((p) => [p.type, p.value]));
        const asUtc = Date.UTC(+v.year, +v.month - 1, +v.day, +v.hour % 24, +v.minute, +v.second);
        return Math.round((asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60000);
    } catch {
        return null;
    }
}

export function deviceTimeZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
}

/** true when this device has the 2026 rules for all four zones, false when not (the phone reports it
 *  in its checkMobileApp check-in, so listMobileAppVersions.js shows which phones are stale). */
export function deviceTzRulesOk(offsetOf = deviceOffsetMinutes) {
    return Object.entries(EXPECTED_OFFSETS).every(([zone, expected]) => offsetOf(zone, PROBE_INSTANT) === expected);
}

export function checkDeviceTz(companyTz, { offsetOf = deviceOffsetMinutes, deviceZone = deviceTimeZone() } = {}) {
    if (!companyTz) return null;
    if (CANADIAN_ZONES.includes(companyTz) && (!deviceZone || UTC_NAMES.includes(deviceZone))) {
        return {
            kind: 'unrecognised',
            message: "This computer's time zone isn't recognised, so Spatchy is showing times in UTC. Install the latest Windows and browser updates, then restart.",
        };
    }
    const expected = EXPECTED_OFFSETS[companyTz];
    if (expected === undefined) return null;
    if (offsetOf(companyTz, PROBE_INSTANT) !== expected) {
        return {
            kind: 'stale',
            message: `This device's time-zone settings are out of date. ${PLACE[companyTz]} no longer changes its clocks from November 1, but this device still thinks it does, so times in Spatchy may show an hour off here. Please update your browser (or your phone's software) and restart it. Saved jobs aren't affected.`,
        };
    }
    return null;
}
